import {
  BoxSelect,
  Gauge,
  Grid3X3,
  Image,
  Link2,
  MessageSquareCode,
  Music2,
  PackageCheck,
  PanelsTopLeft,
  Video,
  WandSparkles,
} from 'lucide-react';
import {
  ChoiceCards,
  Segmented,
  SettingsCard,
  SettingsPageHeader,
  SettingsRow,
  SystemRule,
  Toggle,
} from './SettingsPrimitives';
import PreferenceSyncNotice from './PreferenceSyncNotice';
import usePreferenceNamespace from './usePreferenceNamespace';
// ⚠️ 默认值从 canvasDefaults 引，不在这里再写一份——画布页也要读同一套。
// 两份副本迟早漂开：这边加了一项、画布读到 undefined，而这种漂移不报错，
// 只是默默不生效。
import { CANVAS_PREFERENCE_DEFAULTS as CANVAS_DEFAULTS } from '../../canvas/canvasDefaults';

const PERFORMANCE_OPTIONS = [
  { value: 'quality', label: '质量', description: '完整预览和连接效果', icon: WandSparkles },
  { value: 'balanced', label: '平衡', description: '推荐的大多数画布体验', icon: Gauge },
  { value: 'smooth', label: '流畅', description: '大型画布减少视觉效果', icon: BoxSelect },
];

// 报告建议#2 说「实际创作中 5s、10s、15s 分段生成是常态」，设置里就给这三档；
// 要 6s/12s 这种在节点上单独调，节点级覆盖优先于这里。
const VIDEO_DURATION_OPTIONS = [
  { value: 5, label: '5s' },
  { value: 10, label: '10s' },
  { value: 15, label: '15s' },
];

export default function CanvasSettingsPanel({ preview = false, english = false }) {
  const {
    values,
    status,
    statusLabel,
    updatePreference,
    retry,
  } = usePreferenceNamespace('canvas', CANVAS_DEFAULTS, { enabled: !preview });
  const {
    performance,
    snapGrid,
    edgeMotion,
    openInspector,
    promptOptimize,
    imageRatio,
    videoRatio,
    videoDuration,
    sound,
    autoAsset,
    agentMode,
  } = values;

  return (
    <div className={`account-content__stack creation-settings-page${status === 'loading' ? ' is-loading' : ''}`}>
      <SettingsPageHeader
        eyebrow="CANVAS"
        title={english ? 'Canvas Settings' : '画布设置'}
        description={english ? 'Set canvas interaction and media-generation defaults. Individual nodes can still override them.' : '设置画布交互和媒体生成默认值；单个节点参数仍可临时覆盖。'}
        status={english ? ({ preview: 'Personal settings', loading: 'Loading', ready: 'Synced', saving: 'Saving', saved: 'Saved', conflict: 'Review needed', error: '保存失败' }[status]) : statusLabel}
      />
      <PreferenceSyncNotice preview={preview} status={status} onRetry={retry} english={english} />

      <SettingsCard
        eyebrow="INTERACTION"
        title={english ? 'Canvas Interaction' : '画布交互'}
        description={english ? 'Large projects can reduce visual effects without disabling saving, connection checks, or task status.' : '大型项目可以减少动效，但不会关闭保存、连接校验或任务状态。'}
        aside={english ? 'Personal default' : '个人默认'}
      >
        <ChoiceCards
          label={english ? 'Canvas performance mode' : '画布性能模式'}
          value={performance}
          options={english ? PERFORMANCE_OPTIONS.map((option, index) => ({ ...option, label: ['Quality', 'Balanced', 'Smooth'][index], description: ['Full previews and connection effects', 'Recommended for most canvases', 'Reduce visual effects on large canvases'][index] })) : PERFORMANCE_OPTIONS}
          onChange={value => updatePreference('performance', value)}
        />
        <SettingsRow icon={Grid3X3} title={english ? 'Snap to grid' : '网格吸附'} description={english ? 'Align nodes to the canvas grid while moving.' : '移动节点时对齐到画布网格。'}>
          <Toggle checked={snapGrid} onChange={value => updatePreference('snapGrid', value)} label={english ? 'Snap to grid' : '网格吸附'} />
        </SettingsRow>
        <SettingsRow icon={Link2} title={english ? 'Connection animation' : '连接动画'} description={english ? 'Show data flow while generating or processing.' : '生成或处理中显示数据流向。'}>
          <Toggle checked={edgeMotion} onChange={value => updatePreference('edgeMotion', value)} label={english ? 'Connection animation' : '连接动画'} />
        </SettingsRow>
        <SettingsRow icon={PanelsTopLeft} title={english ? 'Open configuration on selection' : '选中节点后打开配置'} description={english ? 'Expand model, reference, and parameter controls automatically.' : '自动展开模型、参考和参数面板。'}>
          <Toggle checked={openInspector} onChange={value => updatePreference('openInspector', value)} label={english ? 'Open node configuration automatically' : '自动打开节点配置'} />
        </SettingsRow>
        <div className="creation-system-rules">
          <SystemRule
            title={english ? 'Confirm bulk deletion' : '批量删除二次确认'}
            description={english ? 'Bulk deletion always asks for confirmation to prevent removing nodes and connections by mistake.' : '批量删除前始终要求确认，避免误删节点和连接。'}
            status={english ? 'Always on' : '始终开启'}
          />
        </div>
      </SettingsCard>

      <SettingsCard
        eyebrow="GENERATION DEFAULTS"
        title={english ? 'Generation Defaults' : '生成默认值'}
        description={english ? 'Per-task parameters override these personal defaults.' : '任务临时参数优先于这些个人默认值。'}
        aside={english ? 'Personal default' : '个人默认'}
      >
        <SettingsRow icon={MessageSquareCode} title={english ? 'Smart prompt enhancement' : 'Prompt 智能优化'} description={english ? 'Keep the original input and show an editable enhanced version.' : '保留原始输入，并展示优化后的可编辑版本。'}>
          <Toggle checked={promptOptimize} onChange={value => updatePreference('promptOptimize', value)} label={english ? 'Smart prompt enhancement' : 'Prompt 智能优化'} />
        </SettingsRow>
        <SettingsRow icon={Image} title={english ? 'Default image ratio' : '默认图片比例'} description={english ? 'Initial ratio for new image nodes.' : '新建图片节点的初始比例。'}>
          <Segmented
            label={english ? 'Default image ratio' : '默认图片比例'}
            value={imageRatio}
            options={['9:16', '16:9', '1:1']}
            onChange={value => updatePreference('imageRatio', value)}
          />
        </SettingsRow>
        <SettingsRow icon={Video} title={english ? 'Default video ratio' : '默认视频比例'} description={english ? 'Initial ratio for new video nodes.' : '新建视频节点的初始比例。'}>
          <Segmented
            label={english ? 'Default video ratio' : '默认视频比例'}
            value={videoRatio}
            options={['9:16', '16:9', '1:1']}
            onChange={value => updatePreference('videoRatio', value)}
          />
        </SettingsRow>
        <SettingsRow icon={Video} title={english ? 'Default video duration' : '默认视频时长'} description={english ? 'Initial duration for new video nodes. Nodes fed by a storyboard shot follow that shot instead.' : '新建视频节点的初始时长；接了分镜的节点按那场戏的时长走，不用这里的值。'}>
          <Segmented
            label={english ? 'Default video duration' : '默认视频时长'}
            value={videoDuration}
            options={VIDEO_DURATION_OPTIONS}
            onChange={value => updatePreference('videoDuration', value)}
          />
        </SettingsRow>
        <SettingsRow icon={Music2} title={english ? 'Generate video sound by default' : '视频默认生成声音'} description={english ? 'Applies only when the selected model supports sound.' : '仅在当前模型支持声音时生效。'}>
          <Toggle checked={sound} onChange={value => updatePreference('sound', value)} label={english ? 'Generate video sound by default' : '视频默认生成声音'} />
        </SettingsRow>
        <SettingsRow icon={PackageCheck} title={english ? 'Add to Assets after success' : '成功后加入资产中心'} description={english ? 'Keep the result on the canvas and create an account-level asset reference.' : '节点结果保留在画布，同时创建账号级资产引用。'}>
          <Toggle checked={autoAsset} onChange={value => updatePreference('autoAsset', value)} label={english ? 'Add generated results to Assets' : '生成后加入资产中心'} />
        </SettingsRow>
      </SettingsCard>

      <SettingsCard
        eyebrow="CANVAS AGENT"
        title={english ? 'Canvas Agent' : '画布 Agent'}
        description={english ? 'Follow global Agent settings by default or override them for Canvas.' : '默认跟随全局 Agent 设置；可为画布单独覆盖。'}
        aside={english ? 'Personal default' : '个人默认'}
      >
        <SettingsRow icon={WandSparkles} title={english ? 'Default panel mode' : '默认面板形态'} description={english ? 'Switch modes without losing the conversation, selected node, or current task.' : '切换不丢会话、选中节点或当前任务。'}>
          <Segmented
            label={english ? 'Default Canvas Agent mode' : '画布 Agent 默认形态'}
            value={agentMode}
            options={[
              { value: 'follow', label: english ? 'Follow global' : '跟随全局' },
              { value: 'collapsed', label: english ? 'Collapsed' : '收起' },
              { value: 'floating', label: english ? 'Floating' : '浮层' },
              { value: 'max', label: english ? 'Maximized' : '最大化' },
            ]}
            onChange={value => updatePreference('agentMode', value)}
          />
        </SettingsRow>
      </SettingsCard>

      <SettingsCard
        eyebrow="GENERATION RULES"
        title={english ? 'Generation Rules' : '生成规则'}
        description={english ? 'Select an available model in each node and review its estimated credit cost before generating.' : '在节点内选择可用模型，生成前可查看预计消耗点数。'}
      >
        <div className="creation-system-rules">
          <SystemRule
            title={english ? 'Show estimated credits before generation' : '生成前显示预计点数'}
            description={english ? 'Check the estimated credit cost before generating. Tasks cannot start if the balance is insufficient, the model is unavailable, or the input exceeds its limit.' : '生成前确认预计点数；余额不足、模型不可用或输入超限时，需先解决提示的问题再生成。'}
            status={english ? 'Always on' : '不可关闭'}
          />
          <SystemRule
            title={english ? 'Validate reference connection types' : '参考连接类型校验'}
            description={english ? 'Image, video, audio, and prompt connections keep explicit types and never silently ignore inputs.' : '图片、视频、音频和提示词连线保持明确类型，不静默忽略输入。'}
            status={english ? 'System rule' : '系统规则'}
          />
        </div>
      </SettingsCard>
    </div>
  );
}
